/**
 * DESIGN SYSTEM STEP 2 — the printer upgrade's shared numbers.
 *
 * ONE place for every value the editor and the print PDF must agree on: the
 * soft shadow under decorations, the paper background's bleed box, and which
 * shadow is drawn where in the stack. The editor (components/page/PageDecor.tsx)
 * and the PDF (lib/decor/pdfDecor.ts) both import from here, and
 * decorParity.test.ts fails if either stops doing so. The 3D preview is pdf.js
 * rasterising the PDF, so it follows the PDF for free.
 */

/** The album page (trim) in mm. Same as pdfGenerator's PAGE_W/A4_H in pt. */
export const PAGE_MM = { w: 200, h: 297 } as const;
/** Print bleed each side (pdfGenerator's default `bleedMm`). */
export const BLEED_MM = 3;

/** The one shadow every decoration gets (design doc: « the shadow is added the
 *  same way for every piece »). Light from the top-left; offsets are in PAGE
 *  space (they do not turn with the piece). `sigmaMm` is the Gaussian standard
 *  deviation — CSS `blur()` takes exactly that. */
export const SHADOW = {
  offsetXMm: 1.2,
  offsetYMm: 1.8,
  sigmaMm: 1.1,
  color: '#2E2418',
  opacity: 0.32,
} as const;

/** mm → CSS container-query units on the page box (1 cqi = 1 % of the page
 *  width = 2 mm). The page box already establishes `container-type:
 *  inline-size` for text (see the storiz-render-parity skill). */
export function mmToCqi(mm: number): number {
  return (mm / PAGE_MM.w) * 100;
}

/** The paper background's box in % of the TRIM page: the bleed rect. The PDF
 *  cover-fits the image into the bleed (media) box; the editor cover-fits it
 *  into this same rect and lets the page box clip it, so both crop the paper
 *  identically. */
export function backgroundBoxPct(bleedMm: number = BLEED_MM): { left: number; top: number; width: number; height: number } {
  const bx = (bleedMm / PAGE_MM.w) * 100;
  const by = (bleedMm / PAGE_MM.h) * 100;
  return { left: -bx, top: -by, width: 100 + 2 * bx, height: 100 + 2 * by };
}

/** The fields this module reads — a structural subset of PageElement, so the
 *  editor's and the PDF's copies of the type both fit. */
export interface DecorElement {
  id: string;
  type: string;
  imageUrl?: string;
  /** A sticker's lighter copy for the screen (the print uses imageUrl). */
  previewUrl?: string;
  photoId?: string;
  shadow?: boolean;
  group?: string;
  maskUrl?: string;
  /** STEP 3 — 'fill' stretches an icon to its box (its shadow too). */
  fit?: 'fill';
}

/** Shadows exist for image stickers/frames (`icon` with `imageUrl`) and photos
 *  (their box, or their mask's silhouette). Text and shapes never cast one. */
export function shadowable(el: DecorElement): boolean {
  if (!el.shadow) return false;
  if (el.type === 'icon') return !!el.imageUrl;
  return el.type === 'photo';
}

/**
 * Where each shadow is drawn in the element stack: index → the elements whose
 * shadows are painted immediately BEFORE the element at that index.
 *
 * A lone element: its shadow right before itself. A GROUP (e.g. a polaroid =
 * photo + frame on top, both `group: 'g1'`): every member's shadow before the
 * group's first member — so the frame's shadow lands under the photo too, not
 * on top of it. A sticker on the polaroid (no group) casts onto it.
 */
export function shadowPlan<T extends DecorElement>(elements: readonly T[]): Map<number, T[]> {
  const firstOfGroup = new Map<string, number>();
  elements.forEach((e, i) => {
    if (e.group && !firstOfGroup.has(e.group)) firstOfGroup.set(e.group, i);
  });
  const plan = new Map<number, T[]>();
  elements.forEach((e, i) => {
    if (!shadowable(e)) return;
    const at = e.group ? firstOfGroup.get(e.group)! : i;
    const list = plan.get(at) ?? [];
    list.push(e);
    plan.set(at, list);
  });
  return plan;
}

/** A box in mm on the trim page, top-left origin, y down. */
export interface BoxMm { x: number; y: number; w: number; h: number }

/**
 * GROUPS turn as one piece. Every element turns about its OWN centre (editor
 * CSS and print alike), so a polaroid's photo — whose window is not at the
 * frame's centre — would slide out of the window. A model turns a member
 * about the group's pivot by keeping its size and moving its centre where
 * the turn takes it; then `rotation` on the member gives the identical result.
 * Clockwise on screen (y down), like CSS. Work in mm, not %, the page is not
 * square.
 */
export function rotateAboutPivot(box: BoxMm, pivot: { x: number; y: number }, deg: number): BoxMm {
  const th = (deg * Math.PI) / 180;
  const cx = box.x + box.w / 2 - pivot.x;
  const cy = box.y + box.h / 2 - pivot.y;
  const nx = pivot.x + cx * Math.cos(th) - cy * Math.sin(th);
  const ny = pivot.y + cx * Math.sin(th) + cy * Math.cos(th);
  const r = (v: number) => Math.round(v * 1e9) / 1e9;
  return { x: r(nx - box.w / 2), y: r(ny - box.h / 2), w: box.w, h: box.h };
}

/** mm box → PageElement geometry (% of the trim page). */
export function mmBoxToPct(b: BoxMm): { x: number; y: number; width: number; height: number } {
  return { x: (b.x / PAGE_MM.w) * 100, y: (b.y / PAGE_MM.h) * 100, width: (b.w / PAGE_MM.w) * 100, height: (b.h / PAGE_MM.h) * 100 };
}
